import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import { createSoilSolutionAnalysis, getLastSoilSolutionAnalysis } from '../../../Utils/analysis/soilSolutionAnalysis';
import CustomDatePicker from '../../Shared/CustomDatePicker';


const CreateSoilSolution = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { exploitationId } = useParams();
    const [loading, setLoading] = useState(false);
    const [lastAnalysis, setLastAnalysis] = useState(null);



    useEffect(() => {
        const fetchLastAnalysis = async () => {
            try {
                const lastAnalysis = await getLastSoilSolutionAnalysis(exploitationId);
                if (lastAnalysis) setLastAnalysis(lastAnalysis);
            } catch (error) {
                if (error.response.status === 404) {
                    console.log('No previous soil solution analysis found');
                    message.warning('Aucune analyse précédente trouvée');
                    return;
                }
                console.error('Erreur lors de la récupération de la dernière analyse de solution du sol:', error);
                message.error('Échec de la récupération des données de la dernière analyse de solution du sol');
            }
        };

        fetchLastAnalysis();
    }, [exploitationId, form]);

    const duplicateLastAnalysis = () => {
        if (!lastAnalysis) {
            message.error('Aucune analyse précédente trouvée');
            return;
        }

        const prefillData = { ...lastAnalysis };
        delete prefillData.id;
        delete prefillData.date_realisation;

        form.setFieldsValue(prefillData);
        message.success('Analyse précédente dupliquée avec succès');
    };





    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitation_id: parseInt(exploitationId),
            };
            await createSoilSolutionAnalysis(formattedValues);
            message.success('Analyse de solution du sol créée avec succès');
            navigate(`/admin/exploitation/${exploitationId}/analysis/solution-sol`);
        } catch (error) {
            console.error('Erreur lors de la création de l\'analyse de solution du sol:', error);
            message.error('Échec de la création de l\'analyse de solution du sol');
        } finally {
            setLoading(false);
        }
    };

    const soilSolutionParameters = [
        { key: 'calcium', label: 'Calcium' },
        { key: 'magnesium', label: 'Magnésium' },
        { key: 'potassium', label: 'Potassium' },
        { key: 'sodium', label: 'Sodium' },
        { key: 'ammonium', label: 'Ammonium' },
        { key: 'bicarbonates', label: 'Bicarbonates' },
        { key: 'nitrates', label: 'Nitrates' },
        { key: 'chlorures', label: 'Chlorures' },
        { key: 'phosphates', label: 'Phosphates' },
        { key: 'sulfates', label: 'Sulfates' },
        { key: 'fer', label: 'Fer' },
        { key: 'cuivre', label: 'Cuivre' },
        { key: 'manganese', label: 'Manganèse' },
        { key: 'zinc', label: 'Zinc' },
        { key: 'molybdene', label: 'Molybdène' },
        { key: 'bore', label: 'Bore' },
    ];

    const otherParameters = [
        { key: 'somme_anion', label: 'Somme des anions' },
        { key: 'somme_cation', label: 'Somme des cations' },
        { key: 'ph', label: 'pH' },
        { key: 'ec', label: 'Conductivité électrique (EC)' },
    ];

    const HeaderTab = ({ text }) => (
        <th className="border border-gray-300 bg-gray-300 px-2 py-2">{text}</th>
    );

    return (
        <div className="bg-white h-full">
            <Header title="Créer une Analyse de Solution du Sol" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/solution-sol`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />{" "}
                    <div className="font-semibold text-base text-indigo-600">
                        {" "}
                        Retour
                    </div>
                </Link>
                <div className='max-w-[1000px] mx-auto'>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-semibold">Nouvelle Analyse de Solution du Sol</h2>
                        <Button onClick={duplicateLastAnalysis} type='secondary' className='text-md border-indigo-600 border-2 text-indigo-600 hover:bg-indigo-600 hover:text-white'>
                            Dupliquer la dernière analyse
                        </Button>
                    </div>
                    <Form
                        form={form}
                        name="createSoilSolution"
                        onFinish={onFinish}
                        layout="vertical"
                        className='bg-white p-6 rounded-lg max-w-[1000px] shadow-md'
                    >
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                                <CustomDatePicker />
                            </Form.Item>
                            <Form.Item name="laboratoire" label="Laboratoire">
                                <Input />
                            </Form.Item>
                            <Form.Item name="echantillon" label="Échantillon">
                                <Input />
                            </Form.Item>
                            <Form.Item name="vanne_associee" label="Vanne associée">
                                <Input />
                            </Form.Item>
                            <Form.Item name="type_sol" label="Type de sol">
                                <Input />
                            </Form.Item>
                        </div>
                        <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de la solution du sol</h2>
                        <table className="w-full border-collapse border border-gray-300 mb-4">
                            <thead>
                                <tr>
                                    <HeaderTab text="Paramètre" />
                                    <HeaderTab text="Valeur" />
                                    <HeaderTab text="Unité" />
                                </tr>
                            </thead>
                            <tbody>
                                {soilSolutionParameters.map(param => (
                                    <tr key={param.key}>
                                        <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            <Form.Item name={param.key} noStyle>
                                                <InputNumber step={0.01} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2">mg/L</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <h2 className="text-xl font-semibold mt-6 mb-4">Autres paramètres</h2>
                        <table className="w-full border-collapse border border-gray-300 mb-4">
                            <thead>
                                <tr>
                                    <HeaderTab text="Paramètre" />
                                    <HeaderTab text="Valeur" />
                                    <HeaderTab text="Unité" />
                                </tr>
                            </thead>
                            <tbody>
                                {otherParameters.map(param => (
                                    <tr key={param.key}>
                                        <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            <Form.Item name={param.key} noStyle>
                                                <InputNumber step={0.01} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            {param.key === 'ph' ? '' : param.key === 'ec' ? 'mS/cm' : 'meq/L'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Form.Item className="mt-4">
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Créer l'Analyse de Solution du Sol
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default CreateSoilSolution;